import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { readableTypeName } from '@iotv/core-services';
import { AdjacentType, GeoModelCommon, QueryTypes, getObjectHash, removeEmptyArrays, } from '@iotv/datamodel';
import { AppValueTypes, GraphType } from '@iotv/iotv-v3-types';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import { Autocomplete, Button, Card, CardContent, FormControl, FormControlLabel, FormLabel, Grid, Radio, RadioGroup, Switch, TableContainer, TextField, Typography, } from '@mui/material';
import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { v4 } from 'uuid';
import { DialogWrapper } from '../../../../components/common/DiaglogWrapper';
import { genericListViewDefinition, getViewDefinitionFromUserAttributes } from '../../../../components/factories/AttributeDefinitions';
import { commonObjectTypeToVOB } from '../../../../components/io/GoogleV2Hooks/transfromers';
import { SimpleIOModal } from '../../../../components/io/VOBbased/SimpleIOModal';
import { IOType } from '../../../../components/io/types';
import { NavSections } from '../../../../components/navigation/NavSections';
import config from '../../../../config';
import AppDao from '../../../../data/AppDao';
import Assembler from '../../../../data/Assembler';
import { AppLocalStorage } from '../../../../data/LocalStorage/AppLocalStorage';
import ApiGateway from '../../../../data/aws/api-gateway/ApiGateway';
import { deleteObject, link, save } from '../../../../data/daoFunctions/daoFunctions';
import { useObjectByTypeAndName } from '../../../../hooks/useObjectByTypeAndName';
import { addObjectsToState } from '../../../../redux/actions/AppActionFunctions/AppActions';
import { ComponentMode, ContextObjectType, ObjectViewType, } from '../../../../types/AppTypes';
import { modelTypes } from '../../../../util/Model/modelTypes';
import { ObjectDetailCard } from '../ObjectView/ObjectDetailCard';
import { DocClientDataGrid } from './DocClientDataGrid';
import { useColumnVisibilityForDataGrid } from './hooks/useColumnVisibility';
import { useDatastorePersistForDataGrid } from './hooks/useDatastorePersistForDataGrid';
import { useHashReducer } from './useDataGridReducer';
const debug = process.env.REACT_APP_DEBUG && false;
const apiRef = 'ConsumerApi';
const componentClassName = 'DataTableView';
const dyn = config.aws.dynamoDB;
const getViewDefinition = (modelClass) => modelClass ? getViewDefinitionFromUserAttributes(new (modelClass ?? GeoModelCommon)({}).getUserAttributes()) : genericListViewDefinition;
function DataTableView(props) {
    'use no memo';
    // Error: Cannot access refs during render
    const { contextObject: contextObjectIn, transactionFunctions, userFunctions, user, userGroupRoles, userSelectedGroup, constrainedParameters, columnVisibilityReferences, } = props;
    const query = async ({ providedRowObjects, ...queryParams }) => {
        const res = { err: null, data: null };
        const rowObjectCount = providedRowObjects?.length ?? 0;
        if (rowObjectCount > 0) {
            res.data = { Items: providedRowObjects };
            debug && console.log('DataTableView using objects from import');
        }
        else {
            const queryRes = await ApiGateway.post(`/query/`, queryParams, apiRef);
            if (queryRes.data?.Items instanceof Array) {
                res.data = queryRes.data;
            }
            else if (queryRes.err) {
                res.err = queryRes.err;
            }
        }
        return res;
    };
    const params = useParams();
    const typeByLocation = params.type;
    const modelClass = Assembler.getClass(typeByLocation ?? 'nothing ');
    const isValidModelType = modelClass !== undefined;
    const derivedType = constrainedParameters?.objectTypeId
        ? constrainedParameters.objectTypeId
        : isValidModelType
            ? typeByLocation
            : (AppLocalStorage.get(NavSections.DATATABLEVIEW, 'objectTypeId') ?? undefined);
    const [objectTypeId, setObjectTypeId] = React.useState(derivedType);
    const [importedObjects, setImportedObjects] = React.useState([]);
    const [contextObject, setContextObject] = React.useState(constrainedParameters?.searchContextObject);
    const [newObject, setNewObject] = React.useState(undefined);
    const [queryGraphType, setQueryGraphType] = React.useState(constrainedParameters?.queryGraphType ?? AppLocalStorage.get(NavSections.DATATABLEVIEW, 'queryGraphType') ?? GraphType.NODE);
    const [adjacencyType, setAdjacenyType] = React.useState(constrainedParameters?.adjacencyType ?? AdjacentType.CHILD);
    const [viewDefinition, setViewDefinition] = React.useState(getViewDefinition(modelClass ?? GeoModelCommon));
    const [contextObjectType, setContextObjectType] = React.useState(constrainedParameters?.currentContextSearchMode ?? AppLocalStorage.get(NavSections.DATATABLEVIEW, 'contextObjectType') ?? ContextObjectType.NO_CONTEXT);
    const [queryUuid, setQueryUuid] = React.useState(undefined);
    const constrainParametersHash = getObjectHash(constrainedParameters);
    debug && console.log('DataTableView constrainedParameters are', { constrainedParameters, contextObject });
    const getQueryParams = () => {
        const queryParams = {
            providedRowObjects: importedObjects,
            TableName: dyn.tableName,
            ExclusiveStartKey: undefined,
            limit: 50,
            contextObject: contextObject,
            excludeRelatedToObject: undefined,
            includeEdges: queryGraphType === GraphType.EDGE,
            includeNodes: queryGraphType === GraphType.NODE,
            adjacencyType: contextObjectType === ContextObjectType.NO_CONTEXT ? AdjacentType.SUBJECT : adjacencyType,
            objectTypeId: objectTypeId ?? '',
            queryType: QueryTypes.LIST_LIMITED_ALL_TYPES,
        };
        return queryParams;
    };
    const queryParams = getQueryParams();
    debug && console.log(`QUERY PARAMS`, queryParams);
    const mutatedUserFunctions = {
        ...userFunctions,
        addObjectsToState: (vobs) => {
            console.log(`DataTableView, mutatedUserFunctions captured addObjectsToState `, vobs);
            return addObjectsToState([]);
        },
    };
    const [dataGridHash, dispatchDataGridHash] = useHashReducer();
    const selectedTableObjectsRef = React.useRef([]);
    const maxCols = 10;
    const viewDefinitionColumnVisibility = Object.fromEntries(Object.keys(viewDefinition)
        .slice(maxCols)
        .map((k) => [k, false]));
    const { dataTableReference = 'DataTableView', externalColumnVisibility = viewDefinitionColumnVisibility } = columnVisibilityReferences ?? {};
    const [columnVisibilityModel, setColumnVisibilityModel, onColumnVisibilityModelChange] = useColumnVisibilityForDataGrid({
        dataTableReference,
        objectTypeId,
        viewType: ObjectViewType.List,
        externalColumnVisibility,
    });
    const [linkOnSave, setLinkOnSave] = React.useState(AppLocalStorage.get(NavSections.DATATABLEVIEW, 'linkOnSave') ?? true);
    const { onProcessRowUpdateError, processRowUpdate, snackBarState } = useDatastorePersistForDataGrid(mutatedUserFunctions);
    const docClientDataGridProps = {
        queryParams,
        query,
        dependencies: [],
        selectedTableObjectsRef,
        dispatchDataGridHash,
        itemsEditable: true,
        /*processRowUpdate,*/ onProcessRowUpdateError,
        snackBarState,
        columnVisibilityReferences,
        viewDefinition,
        onError: (e) => {
            console.log('DataTableView listened to error', e.message);
        },
        queryUuid,
        parentHasRendered: () => document.querySelector(`.${componentClassName}`) !== null,
    };
    const hiddenColumns = Object.values(columnVisibilityModel).reduce((a, c) => (c === false ? a + 1 : a), 0);
    const handleContextObjectChange = (e) => {
        const valueMap = {
            [ContextObjectType.CUSTOMER]: contextObject,
            [ContextObjectType.USER]: user,
            [ContextObjectType.NO_CONTEXT]: undefined,
            [ContextObjectType.USER_SELECTED]: userSelectedContextObject,
        };
        const selection = e.currentTarget.value;
        setContextObjectType(selection);
        setContextObject(valueMap[selection]);
        AppLocalStorage.set(NavSections.DATATABLEVIEW, 'contextObjectType', selection);
    };
    const handleQueryGraphTypeChange = (e) => {
        const graphType = e.currentTarget.value;
        setQueryGraphType(graphType);
    };
    const handleAdjacencyTypeChange = (e) => {
        const adjacencyType = e.currentTarget.value;
        if (adjacencyType === AdjacentType.SUBJECT) {
            setContextObjectType(ContextObjectType.NO_CONTEXT);
        }
        setAdjacenyType(adjacencyType);
    };
    const currentObject = constrainedParameters?.searchContextObject;
    const currentObjectTypeId = constrainedParameters?.searchContextObject?.type ?? 'Customer';
    const validTypeIds = modelTypes;
    const { components: componentMap, state: { currentEntity: [userSelectedContextObject, setCurrentEntity],
    // objectTypeId: [ objectTypeId, setObjectTypeId ]
     }, } = useObjectByTypeAndName({
        contextObject: contextObject,
        currentObject,
        currentObjectTypeId: currentObjectTypeId,
        validTypeIds: validTypeIds,
        adjacencyType: AdjacentType.SUBJECT,
    });
    useEffect(() => {
        debug && console.log('DataTableView used effect on objectTypeId', objectTypeId);
        setObjectTypeId(objectTypeId);
        const modelClass = Assembler.getClass(objectTypeId ?? 'nothing ');
        setViewDefinition(getViewDefinition(modelClass ?? GeoModelCommon));
    }, [objectTypeId]);
    useEffect(() => { }, [adjacencyType]);
    useEffect(() => {
        if (contextObjectType === ContextObjectType.USER_SELECTED) {
            setContextObject(userSelectedContextObject);
            debug && console.log('Set userSelectedContextObject to ', userSelectedContextObject);
        }
    }, [userSelectedContextObject?.sk /*adjacencyType*/]);
    useEffect(() => {
        if (constrainedParameters?.searchContextObject) {
            setContextObject(constrainedParameters.searchContextObject);
        }
        if (constrainedParameters?.adjacencyType) {
            setAdjacenyType(constrainedParameters.adjacencyType);
        }
        if (constrainedParameters?.queryGraphType) {
            setQueryGraphType(constrainedParameters.queryGraphType);
        }
        if (constrainedParameters?.currentContextSearchMode) {
            setContextObjectType(constrainedParameters.currentContextSearchMode);
        }
        if (constrainedParameters?.objectTypeId) {
            setObjectTypeId(constrainedParameters.objectTypeId);
        }
    }, [constrainParametersHash]);
    /** TODO do handle results */
    const handleSaveSelected = async () => {
        const promises = await Promise.all([...selectedTableObjectsRef.current].map(async (item) => {
            removeEmptyArrays(item);
            const saveRes = await save(item);
            if (linkOnSave && saveRes.data && contextObject) {
                const savedObject = saveRes.data;
                const linkRes = await link(contextObject, savedObject);
                setQueryUuid(v4());
            }
            return saveRes;
        }));
        const errs = promises.filter((promiseRes) => promiseRes.err);
        const datas = promises.filter((promiseRes) => promiseRes.data);
        if (errs.length > 0) {
            userFunctions.setUserMessage({
                type: 'MessageBar',
                content: `Failed to save ${errs.length} items, ${errs.map((err) => err?.err?.message).join(', ')}`,
                label: 'Warning',
                id: v4(),
            });
        }
        if (datas.length > 0) {
            userFunctions.setUserMessage({ type: 'SnackBar', content: `Saved ${datas.length} items`, label: 'Yay', id: v4() });
        }
    };
    /** TODO do handle results */
    const handleDeleteSelected = async () => {
        const promises = await Promise.all([...selectedTableObjectsRef.current].map(async (item) => {
            removeEmptyArrays(item);
            const saveRes = await deleteObject(item);
            setQueryUuid(v4());
            return saveRes;
        }));
    };
    const handleLinkOnSaveChange = () => {
        AppLocalStorage.set(NavSections.DATATABLEVIEW, 'linkOnSave', !linkOnSave);
        setLinkOnSave(!linkOnSave);
    };
    const getRadio = () => (_jsx(Radio, { size: 'small',
        classes: {} }));
    const handleAddNewObject = () => {
        if (objectTypeId) {
            setNewObject(Assembler.getInstance({ type: objectTypeId }));
        }
    };
    const commonButtonProps = { variant: 'contained', color: 'primary' };
    const getNewObjectButton = () => {
        return (_jsx(Button, { ...commonButtonProps,
            startIcon: _jsx(AddCircleIcon, {}),
            onClick: handleAddNewObject,
            children: `Add` }));
    };
    const getObjectTypeSelector = () => (_jsx(Grid, { size: 2, children: typeByLocation || constrainedParameters?.objectTypeId ? (_jsx("div", { children: objectTypeId })) : (_jsx(FormControl, { children: _jsx(Autocomplete, { freeSolo: false,
                value: objectTypeId,
                id: 'objectType-selector',
                options: modelTypes,
                getOptionLabel: (option) => option,
                style: {},
                renderInput: (params) => _jsx(TextField, { ...params, label: 'Object Type', variant: 'outlined', size: 'small', style: { width: '16rem' } }),
                onChange: (e, v) => {
                    debug && console.log('type change', { e, v });
                    AppLocalStorage.set(NavSections.DATATABLEVIEW, 'objectTypeId', v ? v : undefined);
                    setObjectTypeId(v ?? undefined);
                } }) })) }, 'objectTypeSelector'));
    const getModeRadioButtons = () => {
        return constrainedParameters?.searchContextObject
            ? []
            : [
                _jsx(Grid, { size: 2, children: _jsxs(FormControl, { component: "fieldset", size: "small", children: [_jsx(FormLabel, { component: "legend", children: "Context Object" }), _jsxs(RadioGroup, { row: true, "aria-label": "mode", name: "mode", value: contextObjectType, onChange: handleContextObjectChange, children: [_jsx(FormControlLabel, { value: ContextObjectType.USER, control: getRadio(), label: 'User' }, 'User'), _jsx(FormControlLabel, { value: ContextObjectType.CUSTOMER, control: getRadio(), label: 'Customer' }, 'Customer'), _jsx(FormControlLabel, { value: ContextObjectType.NO_CONTEXT, control: getRadio(), label: 'No Context' }, 'NoContext'), _jsx(FormControlLabel, { value: ContextObjectType.USER_SELECTED, control: getRadio(), label: 'User Selected' }, 'UserSelected')] })] }) }, 'modeRadioButtons1'),
                contextObjectType === ContextObjectType.USER_SELECTED && getContextObjectSelector(),
            ];
    };
    const getNodeEdegeRadioButtons = () => {
        return constrainedParameters?.queryGraphType ? null : (_jsx(Grid, { size: 2, children: _jsxs(FormControl, { component: "fieldset", size: "small", children: [_jsx(FormLabel, { component: "legend", children: "Graph Type" }), _jsxs(RadioGroup, { row: true, "aria-label": "mode", name: "mode", value: queryGraphType, onChange: handleQueryGraphTypeChange, children: [_jsx(FormControlLabel, { value: GraphType.NODE, control: getRadio(), label: "Nodes" }), _jsx(FormControlLabel, { value: GraphType.EDGE, control: getRadio(), label: "Edges" })] })] }) }, 'nodeEdegeRadioButtons'));
    };
    const getAdjacencyTypeRadioButtons = () => {
        return constrainedParameters?.adjacencyType ? null : (_jsx(Grid, { size: 2, children: _jsxs(FormControl, { component: "fieldset", size: "small", children: [_jsx(FormLabel, { component: "legend", children: "Adjacency Type" }), _jsxs(RadioGroup, { row: true, "aria-label": "mode", name: "mode", value: adjacencyType, onChange: handleAdjacencyTypeChange, children: [_jsx(FormControlLabel, { value: AdjacentType.CHILD, control: getRadio(), label: "Child" }), _jsx(FormControlLabel, { value: AdjacentType.PARENT, control: getRadio(), label: "Parent" }), _jsx(FormControlLabel, { value: AdjacentType.SUBJECT, control: getRadio(), label: "Subject" })] })] }) }, 'adjacencyTypeRadioButtons'));
    };
    const getContextObjectSelector = () => contextObjectType === ContextObjectType.USER_SELECTED ? (_jsx(Grid, { size: 2, children: [componentMap.objectTypeSelector, componentMap.entitySelector] }, 'contextObjectSelector')) : null;
    const getLinkToggle = () => (_jsx(Grid, { size: 2, children: typeByLocation ? (_jsx("div", {})) : (_jsx(FormControl, { size: 'small', children: _jsx(FormControlLabel, { control: _jsx(Switch, { size: 'small',
                    checked: linkOnSave,
                    onChange: handleLinkOnSaveChange,
                    name: 'contextObjectSwtch',
                    inputProps: { 'aria-label': 'secondary checkbox' } }), label: linkOnSave ? 'link on save' : 'no link on save' }, "contextObject-switch") })) }, 'toggleLink'));
    const queryControls = () => (_jsx(Grid, { container: true, spacing: 2, children: [
            getModeRadioButtons(),
            getObjectTypeSelector(),
            getAdjacencyTypeRadioButtons(),
            getNodeEdegeRadioButtons(),
            getLinkToggle(),
            _jsxs(_Fragment, { children: [" ", hiddenColumns > 0 && _jsx(Typography, { children: `${hiddenColumns} Columns hidden` }), " "] }),
        ] }, 'queryControls'));
    const transformGStringsViaViewDefinition = (pbos) => pbos.map((pbo) => {
        Object.values(viewDefinition).forEach((vkd) => {
            if (vkd.type === AppValueTypes.NUMBER) {
                if (typeof pbo[vkd.key] !== 'number') {
                    const parsedNumber = parseFloat(pbo[vkd.key]);
                    if (!isNaN(parsedNumber)) {
                        pbo[vkd.key] = parsedNumber;
                        debug && console.log('parsed to', parsedNumber);
                    }
                }
            }
        });
        return pbo;
    });
    const hasSelectedItems = selectedTableObjectsRef.current.length > 0; /* TODO */
    const setPlainObjectsInParent = (pobs) => {
        debug && console.log(`Loading ${pobs.length} objects`);
        selectedTableObjectsRef.current = pobs;
        setImportedObjects(pobs.map(commonObjectTypeToVOB));
        setViewDefinition(genericListViewDefinition);
    };
    if (objectTypeId) {
        const adjacentFilter = {
            adjacentType: adjacencyType,
            objectType: objectTypeId,
        };
    }
    const mutatedTransactionFunctions = {
        ...transactionFunctions,
        saveObject: async (vob) => {
            debug && console.log(`DataTableView mutatedTransactionFunctions was delegated save of`, vob);
            if (linkOnSave) {
                if (contextObject) {
                    await AppDao.save(vob).then(() => AppDao.link(contextObject, vob));
                }
                else {
                    await AppDao.save(vob);
                }
            }
            else {
                await AppDao.save(vob);
            }
            setQueryUuid(v4());
        },
    };
    return (_jsxs(_Fragment, { children: [_jsx(Card, { children: _jsxs(CardContent, { children: [process.env.NODE_ENV !== 'production' && `DataTableViewV3`, queryControls(), _jsx(SimpleIOModal, { initialIOType: IOType.EXPORT,
                            columnIndexedKeys: Object.keys(viewDefinition),
                            plainObjects: selectedTableObjectsRef.current,
                            setPlainObjectsInParent,
                            allowIOTypeToggle: true }), hasSelectedItems && (_jsx(FormControl, { children: _jsx(Button, { onClick: handleSaveSelected, variant: 'outlined', children: "Save Selected" }) }, 'saveFormControl')), hasSelectedItems && (_jsx(FormControl, { children: _jsx(Button, { onClick: handleDeleteSelected, variant: 'outlined', children: "Remove Selected" }) }, 'deleteFormControl')), getNewObjectButton(), _jsxs(_Fragment, { children: [_jsx("div", { children: `Selected items; ${selectedTableObjectsRef.current.length} ${selectedTableObjectsRef.current.map((x) => x.name).join(', ')}` }), _jsx("div", { children: `Imported items; ${importedObjects.length}` })] }), _jsx(TableContainer, { children: docClientDataGridProps && _jsx(DocClientDataGrid, { ...docClientDataGridProps }) }, 'tableContainer')] }) }), _jsx(DialogWrapper, { header: `Add ${newObject?.type ? readableTypeName(newObject.type) : 'Entity'} to this table`,
                open: newObject !== undefined,
                setClosed: (actioned) => {
                    debug && console.log('DataTableViewV3: something returned from dialog', actioned);
                    setNewObject(undefined);
                    //setMode(ComponentMode.VIEW)
                }, children: newObject ? (_jsx(ObjectDetailCard, { mostProximateCustomer: undefined,
                    userFunctions,
                    transactionFunctions: mutatedTransactionFunctions,
                    matchedPrimary: newObject,
                    editMode: ComponentMode.EDIT,
                    actions: [] })) : (_jsx(_Fragment, {})) }, 'newObjectDialog')] }));
}
export default DataTableView;
